import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fmtTime, fmtDate, companyDateKey } from '../utils/dateFormatters';
import ConfirmationModal from '../components/ConfirmationModal';
import { useConfirmation } from '../hooks/useConfirmation';
import { useUser } from '../contexts/UserContext';
import { supabase } from '../utils/supabaseClient';
import ModernCard, { ModernCardHeader, ModernCardBody } from '../components/Common/ModernCard';
import StartJobModal from '../components/StartJobModal';
import ExtendJobModal from '../components/ExtendJobModal';
import CompletionPromptModal from '../components/CompletionPromptModal';
import OnHoldModal from '../components/OnHoldModal';
import CancellationModal from '../components/CancellationModal';
import JobPhotosTab from '../components/JobPhotosTab';
import {
  ArrowLeftIcon,
  PhoneIcon,
  MapPinIcon,
  PlayIcon,
  CheckCircleIcon,
  ClockIcon,
  PauseIcon,
  CalendarDaysIcon,
  PlusIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  StopIcon
} from '@heroicons/react/24/outline';
import '../styles/modern-enhancements.css';
import { showToast } from '../components/Common/Toast';
import { WORK_ORDER_STATUS } from '../constants/statusEnums';
import { cancelWorkOrder, canCancelWorkOrder } from '../services/WorkOrderCancellationService';
import { formatCustomerAddress, customerMapsUrl, CUSTOMER_ADDRESS_COLUMNS } from '../utils/customerAddress';
import { serviceAddressRequiredMessage } from '../components/ServiceAddressSelector';
// `rate_cards` carries two column families for the same facts: legacy `name`/`base_rate` and the
// live `service_name`/`rate`. Every writer (RateCardService, both create_default_rate_cards
// overloads) populates the live pair; this page was the only reader of the legacy pair, so every
// card it listed would have shown a blank title and "$undefined". It also printed the raw enum
// ("per HOUR"), which RateCardService already formats correctly for the three other card readers.
import { RateCardService } from '../services/RateCardService';

/**
 * FieldJobView - Employee-focused job execution screen
 * Mobile-first design for technicians to execute jobs from phone/tablet
 */
export default function FieldJobView() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const { modal: confirmDialog, confirm } = useConfirmation();
  
  // State
  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [inventoryItems, setInventoryItems] = useState({});
  const [rateCards, setRateCards] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [employee, setEmployee] = useState(null);
  
  // Modal states
  const [showStartModal, setShowStartModal] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showOnHoldModal, setShowOnHoldModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  
  // Clock In/Out state - persisted in DB
  const [clockedIn, setClockedIn] = useState(false);
  const [clockInTime, setClockInTime] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const timerRef = useRef(null);
  
  // Collapsible sections
  const [expandedSections, setExpandedSections] = useState({
    materials: true,
    rateCards: false,
    timeTracking: true,
    notes: true
  });
  
  // Time log form
  const [showTimeForm, setShowTimeForm] = useState(false);
  const [timeFormData, setTimeFormData] = useState({
    date: companyDateKey(),
    hours: '',
    description: ''
  });
  
  // Note form
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [noteText, setNoteText] = useState('');

  useEffect(() => {
    if (jobId && user?.id) {
      loadJobData();
    }
  }, [jobId, user?.id]);

  // Clock in elapsed time ticker
  useEffect(() => {
    if (clockedIn && clockInTime) {
      timerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - new Date(clockInTime).getTime()) / 1000);
        setElapsedSeconds(elapsed);
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [clockedIn, clockInTime]);

  const loadJobData = async () => {
    setLoading(true);
    try {
      // Two rounds instead of seven reads in a row (page speed 2026-10-25). Round 1 needs only
      // the route and the signed-in user; round 2 needs round 1's answers. Same queries, same
      // filters and the same state updates as before — and nothing is set when the job read
      // fails, as before.
      const [
        { data: jobData, error: jobError },
        { data: itemsData },
        { data: cardsData },
        { data: empData },
      ] = await Promise.all([
        supabase
          .from('work_orders')
          .select('*')
          .eq('id', jobId)
          .single(),
        supabase
          .from('work_order_line_items')
          .select('*')
          .eq('work_order_id', jobId),
        // Rate cards: only the columns the Rate Cards section renders.
        user?.company_id
          ? supabase
            .from('rate_cards')
            .select('id,service_name,description,rate,unit_type')
            .eq('company_id', user.company_id)
            .eq('is_active', true)
          : Promise.resolve({ data: null }),
        // Resolve employee ID (user.id is users.id, not employees.id)
        supabase
          .from('employees')
          .select('id')
          .eq('user_id', user.id)
          .single(),
      ]);

      if (jobError) throw jobError;

      // Inventory items for materials
      const inventoryItemIds = [...new Set((itemsData || [])
        .filter(item => item.inventory_item_id)
        .map(item => item.inventory_item_id))];

      const [customerRes, invRes, tsRes] = await Promise.all([
        // Fetch customer separately (no FK relationship). CUSTOMER_ADDRESS_COLUMNS pulls BOTH
        // address column families — this select used to name the legacy one only, which is why
        // the three reads below rendered "No address" for a billing-only customer.
        jobData?.customer_id
          ? supabase
            .from('customers')
            .select(`name,phone,email,${CUSTOMER_ADDRESS_COLUMNS}`)
            .eq('id', jobData.customer_id)
            .single()
          : null,
        inventoryItemIds.length > 0
          ? supabase
            .from('inventory_items')
            .select('id,name,sku')
            .in('id', inventoryItemIds)
          : null,
        // Timesheets for this job
        empData?.id
          ? supabase
            .from('employee_timesheets')
            .select('*')
            .eq('work_order_id', jobId)
            .eq('employee_id', empData.id)
            .order('created_at', { ascending: false })
          : null,
      ]);

      setJob(jobData);
      if (customerRes) setCustomer(customerRes.data);
      setLineItems(itemsData || []);

      if (invRes) {
        const invMap = {};
        (invRes.data || []).forEach(item => { invMap[item.id] = item; });
        setInventoryItems(invMap);
      }

      if (user?.company_id) setRateCards(cardsData || []);
      setEmployee(empData);

      if (tsRes) {
        const tsData = tsRes.data;
        setTimesheets(tsData || []);

        // Check if there's an active clock-in (clock_in IS NOT NULL AND clock_out IS NULL)
        const activeTimesheet = tsData?.find(ts => ts.clock_in && !ts.clock_out);
        if (activeTimesheet) {
          setClockedIn(true);
          setClockInTime(activeTimesheet.clock_in);
        }
      }

    } catch (error) {
      console.error('Error loading job data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handlers
  const handleStartJob = async () => {
    try {
      const { error } = await supabase
        .from('work_orders')
        // Start (and resume) still stamp "now", but the DATABASE's now: nulling them lets the
        // set_started_at / set_started_timestamp triggers write NOW() into both. Completion's
        // actual_end comes from the DB clock (set_completed_at), so a browser clock running
        // ahead put the start AFTER the end and chk_work_orders_actual_dates (23514) refused
        // to complete the job (2026-10-22, client clock ~6s ahead of the DB).
        .update({
          status: WORK_ORDER_STATUS.IN_PROGRESS,
          started_at: null,
          actual_start: null
        })
        .eq('id', jobId);
      if (error) throw error;
      await loadJobData();
    } catch (error) {
      console.error('Error starting job:', error);
      showToast('error', serviceAddressRequiredMessage(error) || 'Failed to start job');
    }
  };

  const handleStartJobConfirm = async () => {
    await handleStartJob();
    setShowStartModal(false);
  };

  // CompletionPromptModal passes the completion time the tech chose and the work-performed
  // notes. This used to take neither, so a custom end time was ignored and notes were lost.
  const handleCompleteJob = async (completedAt, workPerformed) => {
    // Use RPC to enforce allowed transitions (in_progress → completed)
    try {
      const { error: rpcError } = await supabase.rpc('transition_work_order_status', {
        p_work_order_id: jobId,
        p_new_status: WORK_ORDER_STATUS.COMPLETED
      });
      if (rpcError) throw rpcError;

      // Set completed_at separately (not handled by RPC)
      const completion = { completed_at: completedAt || new Date().toISOString() };
      if (workPerformed) completion.work_performed = workPerformed;
      const { error } = await supabase
        .from('work_orders')
        .update(completion)
        .eq('id', jobId);
      if (error) throw error;
      
      await loadJobData();
    } catch (error) {
      console.error('Error completing job:', error);
      showToast('error', 'Failed to complete job: ' + error.message);
    }
    setShowCompletionModal(false);
  };

  const handleExtendConfirm = async (minutes, reason) => {
    try {
      const currentEnd = new Date(job.scheduled_end || job.scheduled_start);
      currentEnd.setMinutes(currentEnd.getMinutes() + minutes);
      
      // Note: extension_reason column does not exist in work_orders table - only update scheduled_end
      const { error } = await supabase
        .from('work_orders')
        .update({ 
          scheduled_end: currentEnd.toISOString()
          // extension_reason removed - column does not exist in DB
        })
        .eq('id', jobId);
      if (error) throw error;
      await loadJobData();
    } catch (error) {
      console.error('Error extending job:', error);
    }
    setShowExtendModal(false);
  };

  // OnHoldModal calls onConfirm with ONE object. This took (reason, notes), so the whole
  // object landed in on_hold_reason and the notes were dropped.
  const handleOnHoldConfirm = async ({ reason, notes, estimatedResumeDate }) => {
    try {
      // Note: on_hold_at is the correct column name (not on_hold_started_at)
      const { error } = await supabase
        .from('work_orders')
        .update({
          status: WORK_ORDER_STATUS.ON_HOLD,
          on_hold_reason: reason,
          on_hold_notes: notes || null,
          estimated_resume_date: estimatedResumeDate || null,
          on_hold_at: new Date().toISOString()
        })
        .eq('id', jobId);
      if (error) throw error;
      await loadJobData();
    } catch (error) {
      console.error('Error putting job on hold:', error);
    }
    setShowOnHoldModal(false);
  };

  const handleCancellationConfirm = async (cancellationData) => {
    try {
      // Same cancel as every other button (WorkOrderCancellationService). This path never
      // recorded who cancelled, left the calendar event and kept reserved inventory.
      await cancelWorkOrder({
        workOrderId: jobId,
        companyId: user?.company_id,
        status: job?.status,
        userId: user?.id,
        cancellation: cancellationData
      });
      await loadJobData();
      setShowCancelModal(false);
      // Navigate back to dashboard after cancellation
      navigate('/dashboard?view=my');
    } catch (error) {
      console.error('Error cancelling job:', error);
      showToast('error', error?.message || 'Failed to cancel job');
    }
  };

  const handleLogTime = async () => {
    if (!employee?.id || !timeFormData.hours) return;
    try {
      const { error } = await supabase
        .from('employee_timesheets')
        .insert({
          employee_id: employee.id,
          work_order_id: jobId,
          date: timeFormData.date,
          regular_hours: parseFloat(timeFormData.hours),
          notes: timeFormData.description,
          company_id: user.company_id,
          status: 'submitted'
        });
      if (error) throw error;
      await loadJobData();
      setShowTimeForm(false);
      setTimeFormData({ date: companyDateKey(), hours: '', description: '' });
    } catch (error) {
      console.error('Error logging time:', error);
    }
  };

  // Clock In - creates timesheet row with clock_in = NOW()
  const handleClockIn = async () => {
    if (!employee?.id) {
      console.error('No employee ID found');
      return;
    }
    try {
      // One running clock per person (#5836, operator ruling 2026-10-17, industry standard):
      // clocking in here while still on the clock for another job left two open entries, and
      // both hours counted. As Jobber/HCP do, offer to clock out of the other job first.
      const { data: openElsewhere } = await supabase
        .from('employee_timesheets')
        .select('id, clock_in, work_order_id, work_orders(title)')
        .eq('company_id', user.company_id)
        .eq('employee_id', employee.id)
        .is('clock_out', null)
        .not('clock_in', 'is', null)
        // an open entry with no job (general time) counts too; neq alone skips NULLs
        .or(`work_order_id.is.null,work_order_id.neq.${jobId}`)
        .limit(1);
      const other = openElsewhere?.[0];
      if (other) {
        const ok = await confirm({
          title: 'You are still clocked in',
          message: `You are still on the clock for "${other.work_orders?.title || 'another job'}" since ${fmtTime(other.clock_in)}. Clock out of it and start this job?`,
          confirmText: 'Switch jobs'
        });
        if (ok !== 'confirm') return;
        const outAt = new Date().toISOString();
        const hours = parseFloat(((new Date(outAt) - new Date(other.clock_in)) / 3600000).toFixed(2));
        const { error: outError } = await supabase
          .from('employee_timesheets')
          .update({ clock_out: outAt, regular_hours: hours, status: 'submitted' })
          .eq('id', other.id)
          .eq('company_id', user.company_id);
        if (outError) throw outError;
      }

      const { error } = await supabase
        .from('employee_timesheets')
        .insert({
          employee_id: employee.id,
          work_order_id: jobId,
          // The company's day, not the UTC one (a 9pm Eastern clock-in was dated tomorrow).
          date: companyDateKey(new Date()),
          clock_in: new Date().toISOString(),
          clock_out: null,
          company_id: user.company_id,
          status: 'draft'
        });
      if (error) throw error;
      
      // Reload to get the created record
      await loadJobData();
    } catch (error) {
      console.error('Error clocking in:', error);
      showToast('error', 'Failed to clock in:');
    }
  };

  // Clock Out - updates timesheet row with clock_out = NOW() and calculates hours
  const handleClockOut = async () => {
    if (!clockInTime || !employee?.id) return;
    
    try {
      // Find the active timesheet
      const { data: activeSheets } = await supabase
        .from('employee_timesheets')
        .select('*')
        .eq('work_order_id', jobId)
        .eq('employee_id', employee.id)
        .is('clock_out', null)
        .not('clock_in', 'is', null);
      
      if (!activeSheets || activeSheets.length === 0) {
        console.error('No active timesheet found');
        return;
      }
      
      const timesheet = activeSheets[0];
      const clockOutTime = new Date().toISOString();
      
      // Calculate regular_hours from the difference
      const clockIn = new Date(clockInTime);
      const clockOut = new Date(clockOutTime);
      const hoursDiff = (clockOut.getTime() - clockIn.getTime()) / (1000 * 60 * 60);
      const regularHours = parseFloat(hoursDiff.toFixed(2));
      
      const { error } = await supabase
        .from('employee_timesheets')
        .update({
          clock_out: clockOutTime,
          regular_hours: regularHours,
          status: 'submitted'
        })
        .eq('id', timesheet.id);
      
      if (error) throw error;
      
      setClockedIn(false);
      setClockInTime(null);
      setElapsedSeconds(0);
      
      // Reload to refresh timesheets list
      await loadJobData();
    } catch (error) {
      console.error('Error clocking out:', error);
      showToast('error', 'Failed to clock out:');
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    try {
      const currentNotes = job?.notes || '';
      const newNotes = currentNotes + (currentNotes ? '\n\n' : '') + 
        `[${new Date().toLocaleString()}] ${user.full_name}: ${noteText}`;
      
      const { error } = await supabase
        .from('work_orders')
        .update({ notes: newNotes })
        .eq('id', jobId);
      if (error) throw error;
      await loadJobData();
      setNoteText('');
      setShowNoteForm(false);
    } catch (error) {
      console.error('Error adding note:', error);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Group line items by type
  const groupedLineItems = lineItems.reduce((acc, item) => {
    const type = item.line_type || 'other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(item);
    return acc;
  }, {});

  // Format timer display
  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  // Status badge
  const StatusBadge = ({ status }) => {
    const colors = {
      [WORK_ORDER_STATUS.SCHEDULED]: 'bg-purple-500/20 text-purple-300 border border-purple-500/30',
      [WORK_ORDER_STATUS.IN_PROGRESS]: 'bg-orange-500/20 text-orange-300 border border-orange-500/30',
      [WORK_ORDER_STATUS.COMPLETED]: 'bg-green-500/20 text-green-300 border border-green-500/30',
      [WORK_ORDER_STATUS.ON_HOLD]: 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${colors[status] || 'bg-gray-500/20 text-gray-300 border border-gray-500/30'}`}>
        {status?.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-20 bg-white/5 rounded-lg"></div>
          <div className="h-40 bg-white/5 rounded-lg"></div>
          <div className="h-60 bg-white/5 rounded-lg"></div>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-slate-900 p-4">
        <div className="text-center py-12">
          <p className="text-gray-400">Job not found</p>
          <button onClick={() => navigate('/dashboard?view=my')} className="text-blue-400 mt-4">
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 pb-24">
      {/* Header */}
      <div className="bg-white/5 border-b border-white/10 backdrop-blur-sm sticky top-0 z-10">
        <div className="p-4">
          {/* Two ways out, because this screen had only one. This is the WORK console —
              start, complete, log time, notes — and it deliberately stays that way; the
              job's full record (customer, money, scope, schedule) lives at /jobs/:id.
              Anyone who lands here and wants the record had no path to it. */}
          <div className="mb-3 flex items-center justify-between gap-3">
            <button
              onClick={() => navigate('/dashboard?view=my')}
              className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5" />
              <span className="text-sm">Back to Dashboard</span>
            </button>
            <button
              onClick={() => navigate(`/jobs/${jobId}`)}
              className="text-sm text-blue-300 hover:text-blue-200 hover:underline transition-colors"
            >
              View job record
            </button>
          </div>
          
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="text-xl font-bold text-white">{job.title || 'Service Call'}</h1>
              <p className="text-sm text-gray-400">WO #{job.work_order_number || job.id}</p>
            </div>
            <StatusBadge status={job.status} />
          </div>

          {/* Customer Info */}
          <div className="mt-3 space-y-1">
            <div className="flex items-center gap-2 text-sm text-gray-300">
              <span className="font-medium">{customer?.name || 'Customer'}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <MapPinIcon className="w-4 h-4" />
              <span>{formatCustomerAddress(customer) || 'No address'}</span>
            </div>
          </div>

          {/* Scheduled Time */}
          <div className="mt-3 flex items-center gap-2 text-sm text-gray-400">
            <CalendarDaysIcon className="w-4 h-4" />
            {job.scheduled_start && (
              <span>
                {fmtDate(job.scheduled_start)} - {fmtTime(job.scheduled_start)}
                {job.scheduled_end && ` to ${fmtTime(job.scheduled_end)}`}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="p-4 space-y-3">
        {/* Quick Actions Row */}
        <div className="grid grid-cols-2 gap-3">
          {customer?.phone && (
            <a 
              href={`tel:${customer.phone}`}
              className="flex items-center justify-center gap-2 p-3 bg-blue-600 text-white rounded-lg font-medium"
            >
              <PhoneIcon className="w-5 h-5" />
              Call
            </a>
          )}
          {/* Navigate used to pass the street line alone, so a tech got "123 Main St" with no
              city or state to geocode. customerMapsUrl sends the full address. */}
          {customerMapsUrl(customer) && (
            <a
              href={customerMapsUrl(customer)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 p-3 bg-green-600 text-white rounded-lg font-medium"
            >
              <MapPinIcon className="w-5 h-5" />
              Navigate
            </a>
          )}
        </div>

        {/* Status-based Actions */}
        {job.status === WORK_ORDER_STATUS.SCHEDULED && (
          <button
            onClick={() => setShowStartModal(true)}
            className="w-full flex items-center justify-center gap-2 p-4 bg-orange-600 text-white rounded-lg font-semibold text-lg"
          >
            <PlayIcon className="w-6 h-6" />
            Start Job
          </button>
        )}

        {job.status === WORK_ORDER_STATUS.IN_PROGRESS && (
          <div className="space-y-3">
            <button
              onClick={() => setShowCompletionModal(true)}
              className="w-full flex items-center justify-center gap-2 p-4 bg-green-600 text-white rounded-lg font-semibold text-lg"
            >
              <CheckCircleIcon className="w-6 h-6" />
              Complete Job
            </button>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setShowExtendModal(true)}
                className="flex items-center justify-center gap-2 p-3 bg-blue-600 text-white rounded-lg font-medium"
              >
                <ClockIcon className="w-5 h-5" />
                Extend
              </button>
              <button
                onClick={() => setShowOnHoldModal(true)}
                className="flex items-center justify-center gap-2 p-3 bg-yellow-600 text-white rounded-lg font-medium"
              >
                <PauseIcon className="w-5 h-5" />
                Hold
              </button>
            </div>
          </div>
        )}

        {job.status === WORK_ORDER_STATUS.ON_HOLD && (
          <button
            onClick={handleStartJob}
            className="w-full flex items-center justify-center gap-2 p-4 bg-orange-600 text-white rounded-lg font-semibold text-lg"
          >
            <PlayIcon className="w-6 h-6" />
            Resume Job
          </button>
        )}

        {/* Cancel Job button - subtle but accessible; shown whenever the job can still be cancelled */}
        {canCancelWorkOrder(job.status) && (
          <button
            onClick={() => setShowCancelModal(true)}
            className="w-full mt-2 flex items-center justify-center gap-2 p-3 border border-red-500/50 text-red-400 rounded-lg font-medium hover:bg-red-500/10 transition-colors"
          >
            <StopIcon className="w-5 h-5" />
            Cancel Job
          </button>
        )}
      </div>

      {/* Job Brief */}
      <div className="px-4 mb-4">
        <ModernCard>
          <ModernCardHeader title="Job Brief" gradient="purple" />
          <ModernCardBody>
            {/* Scope of Work */}
            <div className="mb-4">
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Scope of Work</h4>
              <p className="text-sm text-gray-200">
                {job.description || <span className="italic text-gray-500">No scope of work provided</span>}
              </p>
            </div>
            
            {/* Special Instructions */}
            {job.special_instructions && (
              <div className="mb-4 p-3 bg-yellow-900/20 border border-yellow-600/30 rounded-lg">
                <h4 className="text-xs font-semibold text-yellow-400 uppercase mb-1">⚠️ Special Instructions</h4>
                <p className="text-sm text-yellow-200">{job.special_instructions}</p>
              </div>
            )}
            
            {/* Service Address */}
            <div className="mb-4">
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Service Location</h4>
              <p className="text-sm text-gray-200">
                {job.service_address_line_1
                  ? `${job.service_address_line_1}, ${job.service_city || ''} ${job.service_state || ''} ${job.service_zip_code || ''}`.trim()
                  : formatCustomerAddress(customer) || 'No address provided'}
              </p>
            </div>

            {/* Estimated Duration */}
            {(job.estimated_duration) && (
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Estimated Duration</h4>
                <p className="text-sm text-gray-200">{job.estimated_duration} hours</p>
              </div>
            )}
          </ModernCardBody>
        </ModernCard>
      </div>

      {/* Materials & Line Items */}
      <div className="px-4 mb-4">
        <ModernCard>
          <ModernCardHeader 
            title="Materials & Line Items"
            gradient="blue"
            action={
              <button onClick={() => toggleSection('materials')} className="p-1 text-gray-400 hover:text-white">
                {expandedSections.materials ? <ChevronUpIcon className="w-5 h-5" /> : <ChevronDownIcon className="w-5 h-5" />}
              </button>
            }
          />
          {expandedSections.materials && (
            <ModernCardBody>
              {lineItems.length === 0 ? (
                <div className="p-3 bg-amber-900/20 border border-amber-600/30 rounded-lg">
                  <p className="text-amber-300 text-sm">⚠️ No materials or parts specified for this job. Check with your supervisor if you need to bring supplies.</p>
                </div>
              ) : (
                Object.entries(groupedLineItems).map(([type, items]) => (
                  <div key={type} className="mb-4 last:mb-0">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">{type}</h4>
                    {items.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2 border-b border-white/10 last:border-0">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-200">{item.description}</p>
                          {item.inventory_item_id && inventoryItems[item.inventory_item_id] && (
                            <p className="text-xs text-gray-500">SKU: {inventoryItems[item.inventory_item_id].sku}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-300">Qty: {item.quantity || 1}</p>
                          {item.unit_price && (
                            <p className="text-xs text-gray-500">${item.unit_price}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ))
              )}
            </ModernCardBody>
          )}
        </ModernCard>
      </div>

      {/* Rate Cards Reference */}
      <div className="px-4 mb-4">
        <ModernCard>
          <ModernCardHeader 
            title="Rate Cards"
            gradient="purple"
            action={
              <button onClick={() => toggleSection('rateCards')} className="p-1 text-gray-400 hover:text-white">
                {expandedSections.rateCards ? <ChevronUpIcon className="w-5 h-5" /> : <ChevronDownIcon className="w-5 h-5" />}
              </button>
            }
          />
          {expandedSections.rateCards && (
            <ModernCardBody>
              {rateCards.length === 0 ? (
                <p className="text-gray-400 text-sm">No rate cards available</p>
              ) : (
                <div className="space-y-2">
                  {rateCards.map((card) => (
                    <div key={card.id} className="flex items-center justify-between py-2 border-b border-white/10 last:border-0">
                      <div>
                        {/* service_name/rate, not name/base_rate — see the unit-label note below */}
                        <p className="text-sm font-medium text-gray-200">{card.service_name}</p>
                        <p className="text-xs text-gray-500">{card.description}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-gray-200">
                          {RateCardService.formatRateDisplay(card.rate, card.unit_type)}
                        </p>
                        <p className="text-xs text-gray-500">
                          {RateCardService.getUnitTypes().find(u => u.value === card.unit_type)?.label || card.unit_type}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ModernCardBody>
          )}
        </ModernCard>
      </div>

      {/* Time Tracking */}
      <div className="px-4 mb-4">
        <ModernCard>
          <ModernCardHeader 
            title="Time Tracking"
            gradient="green"
            action={
              <button onClick={() => toggleSection('timeTracking')} className="p-1 text-gray-400 hover:text-white">
                {expandedSections.timeTracking ? <ChevronUpIcon className="w-5 h-5" /> : <ChevronDownIcon className="w-5 h-5" />}
              </button>
            }
          />
          {expandedSections.timeTracking && (
            <ModernCardBody>
              {/* Clock In/Out Display */}
              <div className="bg-slate-800/50 rounded-lg p-4 mb-4 text-center border border-white/10">
                <p className="text-3xl font-mono text-green-400">{formatTime(elapsedSeconds)}</p>
                {clockedIn ? (
                  <button
                    onClick={handleClockOut}
                    className="mt-2 flex items-center justify-center gap-2 w-full p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <StopIcon className="w-5 h-5" />
                    Clock Out
                  </button>
                ) : (
                  <button
                    onClick={handleClockIn}
                    className="mt-2 flex items-center justify-center gap-2 w-full p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <PlayIcon className="w-5 h-5" />
                    Clock In
                  </button>
                )}
              </div>

              {/* Existing Timesheets */}
              <div className="mb-4">
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Logged Time</h4>
                {timesheets.length === 0 ? (
                  <p className="text-gray-500 text-sm">No time logged yet</p>
                ) : (
                  <div className="space-y-2">
                    {timesheets.map((ts) => (
                      <div key={ts.id} className="flex items-center justify-between py-2 border-b border-white/10 last:border-0">
                        <div>
                          <p className="text-sm text-gray-300">{ts.date}</p>
                          {ts.clock_in && (
                            <p className="text-xs text-gray-500">
                              {fmtTime(ts.clock_in)}
                              {ts.clock_out ? ` → ${fmtTime(ts.clock_out)}` : ' → In progress'}
                            </p>
                          )}
                          {ts.notes && <p className="text-xs text-gray-500 mt-0.5">{ts.notes}</p>}
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-300">{Number(ts.regular_hours || 0).toFixed(2)}h</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Manual Time Entry */}
              {showTimeForm ? (
                <div className="bg-white/5 rounded-lg p-3 space-y-3 border border-white/10">
                  <input
                    type="date"
                    value={timeFormData.date}
                    onChange={(e) => setTimeFormData({ ...timeFormData, date: e.target.value })}
                    className="w-full p-2 bg-white/5 border border-white/10 rounded-lg text-white [color-scheme:dark]"
                  />
                  <input
                    type="number"
                    step="0.25"
                    placeholder="Hours"
                    value={timeFormData.hours}
                    onChange={(e) => setTimeFormData({ ...timeFormData, hours: e.target.value })}
                    className="w-full p-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500"
                  />
                  <input
                    type="text"
                    placeholder="Description (optional)"
                    value={timeFormData.description}
                    onChange={(e) => setTimeFormData({ ...timeFormData, description: e.target.value })}
                    className="w-full p-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowTimeForm(false)}
                      className="flex-1 p-2 border border-white/20 rounded-lg text-gray-300 hover:bg-white/5"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleLogTime}
                      className="flex-1 p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowTimeForm(true)}
                  className="w-full flex items-center justify-center gap-2 p-3 border border-white/20 rounded-lg text-gray-300 hover:bg-white/5"
                >
                  <PlusIcon className="w-5 h-5" />
                  Log Time Manually
                </button>
              )}
            </ModernCardBody>
          )}
        </ModernCard>
      </div>

      {/* Notes & Photos */}
      <div className="px-4 mb-4">
        <ModernCard>
          <ModernCardHeader 
            title="Notes & Photos"
            gradient="amber"
            action={
              <button onClick={() => toggleSection('notes')} className="p-1 text-gray-400 hover:text-white">
                {expandedSections.notes ? <ChevronUpIcon className="w-5 h-5" /> : <ChevronDownIcon className="w-5 h-5" />}
              </button>
            }
          />
          {expandedSections.notes && (
            <ModernCardBody>
              {/* Notes Display */}
              <div className="mb-4">
                {job.notes ? (
                  <div className="text-sm text-gray-300 whitespace-pre-wrap bg-white/5 p-3 rounded-lg border border-white/10">
                    {job.notes}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No notes yet</p>
                )}
              </div>

              {/* Add Note Form */}
              {showNoteForm ? (
                <div className="space-y-3">
                  <textarea
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Add a note..."
                    className="w-full p-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500"
                    rows={3}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowNoteForm(false)}
                      className="flex-1 p-2 border border-white/20 rounded-lg text-gray-300 hover:bg-white/5"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddNote}
                      className="flex-1 p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Save Note
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowNoteForm(true)}
                  className="w-full flex items-center justify-center gap-2 p-3 border border-white/20 rounded-lg text-gray-300 hover:bg-white/5"
                >
                  <PlusIcon className="w-5 h-5" />
                  Add Note
                </button>
              )}

              {/* Photos - Wired up to JobPhotosTab */}
              <div className="mt-4 pt-4 border-t border-white/20">
                <JobPhotosTab workOrderId={jobId} />
              </div>
            </ModernCardBody>
          )}
        </ModernCard>
      </div>

      {/* Modals */}
      <StartJobModal
        isOpen={showStartModal}
        onClose={() => setShowStartModal(false)}
        onConfirm={handleStartJobConfirm}
        jobTitle={job.title}
        scheduledStart={job.scheduled_start}
      />

      <ExtendJobModal
        isOpen={showExtendModal}
        onClose={() => setShowExtendModal(false)}
        onConfirm={handleExtendConfirm}
        jobTitle={job.title}
        currentScheduledEnd={job.scheduled_end}
        jobId={job.id}
        assignedTo={job.assigned_to}
        companyId={user?.company_id}
      />

      <CompletionPromptModal
        isOpen={showCompletionModal}
        onClose={() => setShowCompletionModal(false)}
        onCreateInvoice={() => setShowCompletionModal(false)}
        onMarkComplete={handleCompleteJob}
        onExtendJob={() => { setShowCompletionModal(false); setShowExtendModal(true); }}
        jobTitle={job.title}
        allowInvoice={false}
        initialWorkPerformed={job.work_performed || ''}
        workOrderId={job.id}
        customerId={job.customer_id}
      />

      <OnHoldModal
        isOpen={showOnHoldModal}
        onClose={() => setShowOnHoldModal(false)}
        onConfirm={handleOnHoldConfirm}
        jobTitle={job.title}
      />

      <CancellationModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancellationConfirm}
        jobTitle={job.title}
        workOrderNumber={job.work_order_number || null}
      />
      <ConfirmationModal isOpen={!!confirmDialog?.isOpen} {...(confirmDialog || {})} />
    </div>
  );
}
